import React from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { wrapper } from 'src/redux'
import { Head, LinkRel, MetaTags } from 'src/components/common';
import {
  Box,
  Heading,
  Text,
  Divider,
  Grid,
  GridItem,
} from '@chakra-ui/react'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { theme } from 'src/themes'
import { getFaqIndexData } from 'src/utils/getStaticProps'
import { Faq } from 'src/components/common/StructuredData'

const Autocomplete = dynamic(() => import('src/components/Autocomplete/'))
const Autopromo = dynamic(() => import('src/components/NltOffers/AutoPromo'))

interface Props {
  faq: {
    items: Array<any>,
    highlights: Array<any>
  }
  searchAutoCompleteItems: Array<any>
}

const FaqNltPage: NextPage<Props> = ({ faq, searchAutoCompleteItems }) => {
  const { t } = useTranslation()
  const router = useRouter()

  const { items, highlights } = faq

  return <Box bgColor="baseBackgroundLight" mt="-2">
    <Head title={t('faqPage:title')} />
    <MetaTags meta={t('faqPage:meta', { returnObjects: true })} />
    <LinkRel rel="canonical" href="https://www.carplanner.com/noleggio-a-lungo-termine/faq" />
    <Faq />

    <Box textAlign="center" bg="primaryGreen.50" position="sticky" top="0" zIndex="9">
      <Box maxW="7xl" py="l" mx="auto">
        <Heading as="h1" fontSize="2xl" fontFamily="Montserrat-SemiBold" color="baseDark">
          {t('faqPage:title')}
        </Heading>

        <Autocomplete
          items={searchAutoCompleteItems.map(item => ({
            label: `${item.q} - ${item.a}`,
            value: `/noleggio-a-lungo-termine/faq/${item.uri}#${item.anchor}`
          }))}
          onSelect={selected => router.push(selected)}
          placeholder={t('faqPage:placeholder')}
          notFoundText={t('faqPage:notFound')}
          mt="l"
          mx="auto"
          w="50%"
          minW="300px"
        />
      </Box>
    </Box>

    <Box maxW="7xl" py="l" mx={{ base: 'l', md: 'auto' }}>
      <Text fontFamily="Montserrat-SemiBold" fontSize="1.375rem" color="baseDark" textAlign="left">
        {t('faqPage:highlights')}
      </Text>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
        gap={{ base: '3', md: '6' }}
        pt="l"
      >
        {highlights.map(highlight =>
          <GridItem key={`${highlight.uri}${highlight.anchor}`}>
            <Autopromo
              item={{
                code: highlight.anchor,
                layout: 'solid',
                title: highlight.q,
                text: ' ',
                link: `/noleggio-a-lungo-termine/faq/${highlight.uri}#${highlight.anchor}`,
                linkText: 'Visualizza FAQ',
                image: highlight.image || '/icons/icon_customer.svg'
              }} />
          </GridItem>
        )}
      </Grid>

      <Divider my="xl" />

      {items.map(item =>
        <Box
          key={`${item.uri}`}
          borderRadius="10"
          bg="baseWhite"
          p="l"
          my="s"
          borderWidth="1px"
          borderColor={theme.colors.borders.grey}
          boxShadow={theme.shadows.cards}
          textAlign="left"
          cursor="pointer"
          onClick={() => window.location.href = `/noleggio-a-lungo-termine/faq/${item.uri}`}
        >
          <Text fontFamily="Montserrat-SemiBold" mr="l">
            {item.title}
          </Text>
          <Box float="right" mt="-8" fontSize="2xl">
            <ArrowForwardIcon />
          </Box>
        </Box>
      )}

    </Box>
  </Box>
}

export const getStaticProps = wrapper.getStaticProps(() => async () => {
  const content = await getFaqIndexData()

  return { props: { ...content }, revalidate: 60 * 5 }
});

export default FaqNltPage
